import type {
  OperationProgressStatus,
  OperationProgressStepStatus,
} from './operationProgress.types';

export type OperationProgressStatusChipColor =
  | 'default'
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

export type OperationProgressStepIconColor =
  | 'disabled'
  | 'primary'
  | 'success'
  | 'warning'
  | 'error';

export const isOperationProgressBusy = (status: OperationProgressStatus): boolean =>
  status === 'pending' || status === 'waiting' || status === 'running';

export const getOperationProgressStatusLabel = (status: OperationProgressStatus): string => {
  switch (status) {
    case 'pending':
      return 'Starting';
    case 'waiting':
      return 'Waiting';
    case 'running':
      return 'In progress';
    case 'complete':
      return 'Complete';
    case 'cached':
      return 'Cached';
    case 'warning':
      return 'Needs review';
    case 'error':
      return 'Stopped';
    default:
      return 'In progress';
  }
};

export const getOperationProgressStatusChipColor = (
  status: OperationProgressStatus
): OperationProgressStatusChipColor => {
  switch (status) {
    case 'complete':
      return 'success';
    case 'cached':
      return 'info';
    case 'warning':
    case 'waiting':
      return 'warning';
    case 'error':
      return 'error';
    case 'pending':
    case 'running':
      return 'primary';
    default:
      return 'default';
  }
};

export const getOperationProgressStepIconColor = (
  status: OperationProgressStepStatus
): OperationProgressStepIconColor => {
  switch (status) {
    case 'complete':
      return 'success';
    case 'current':
      return 'primary';
    case 'skipped':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'disabled';
  }
};

export const formatOperationProgressDuration = (
  ms: number | null | undefined
): string | null => {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) {
    return null;
  }

  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
};
